import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useNoIndex } from '@/hooks/useSEO';

/* /secret-viral-welcome — hidden landing page for QR code marketing campaigns.
   noindex,follow so it never shows up in search results. The route exists
   only when reached via a printed or shared QR code. */

export default function SecretViralWelcome() {
    useNoIndex('A small surprise for the children in your life — Linguistic Quest');

    return (
        <main className="bg-lq-paper min-h-[80vh] grain-bg" data-testid="page-secret-viral">
            <section className="relative overflow-hidden">
                <div className="hero-orb w-96 h-96 bg-lq-peach-500 -top-24 -right-20" />
                <div className="hero-orb w-80 h-80 bg-lq-purple-300 -bottom-24 -left-10" />
                <div className="relative max-w-3xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24 text-center">
                    <span className="inline-flex items-center gap-2 bg-white border-2 border-lq-peach-300 text-lq-peach-600 text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full" data-testid="secret-eyebrow">
                        <Sparkles size={14} /> A small surprise
                    </span>
                    <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] text-lq-ink-black" data-testid="secret-h1">
                        Now that we know you like things,
                        <br />
                        we hope you like this for the children in your life.
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-lq-ink-700 max-w-2xl mx-auto leading-relaxed">
                        Linguistic Quest is a calm, family-driven puzzle game that teaches heritage and world languages
                        &mdash; Shona, Ndebele, Yoruba, Swahili, French and Portuguese &mdash; one short, joyful session at a time.
                    </p>
                    <p className="mt-4 text-base text-lq-ink-700 max-w-xl mx-auto leading-relaxed">
                        No streaks. No timers. No pressure. Just words, voices and the chance for a child to hear a language that&rsquo;s waited a generation to be passed on.
                    </p>

                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link to="/" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-lq-purple-300/30 transition-colors" data-testid="secret-cta-home">
                            Take me to Linguistic Quest <ArrowRight size={18} />
                        </Link>
                        <Link to="/parents" className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-7 py-3.5 rounded-full transition-colors" data-testid="secret-cta-parents">
                            For parents &amp; carers
                        </Link>
                    </div>

                    <div className="mt-12 inline-flex items-center justify-center bg-white border border-lq-ink-100 rounded-2xl px-6 py-4 text-sm text-lq-ink-500 max-w-md mx-auto">
                        <em>&ldquo;Mhoro, muzukuru. Tinotamba pamwe chete.&rdquo;</em>
                        <br />
                    </div>
                    <p className="mt-3 text-xs text-lq-ink-500 italic">Hello, grandchild. Let&rsquo;s play together. &mdash; Gogo &amp; Sekuru</p>

                    <p className="mt-12 text-xs text-lq-ink-500">
                        Free to start &middot; No card needed &middot; Up to five children per family on paid plans
                    </p>
                </div>
            </section>
        </main>
    );
}
